import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { guardrailItems, guardrailItemsMobile } from '../mockData';

const GuardrailsModal = ({ showGuardrails, onClose, onSave, guardrails, isMobile, isReadOnly = false }) => {
  // Default guardrails structure
  const defaultGuardrails = {
    stopOnReply: true,
    stopOnStatusChange: {
      enabled: true,
      statuses: ['closed', 'archived']
    },
    stopOnWeekend: false
  };

  // Local state for editing guardrails
  const [localGuardrails, setLocalGuardrails] = useState(guardrails || defaultGuardrails);

  // Read-only toast state
  const [showReadOnlyToast, setShowReadOnlyToast] = useState(false);
  const toastTimerRef = React.useRef(null);

  const triggerReadOnlyToast = () => {
    setShowReadOnlyToast(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setShowReadOnlyToast(false), 2500);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); };
  }, []);

  // Sync with props when modal opens
  useEffect(() => {
    if (showGuardrails) {
      setLocalGuardrails(guardrails || defaultGuardrails);
    }
  }, [showGuardrails, guardrails]);

  if (!showGuardrails) return null;

  const items = isMobile ? guardrailItemsMobile : guardrailItems;

  const handleToggleStopOnReply = () => {
    if (isReadOnly) { triggerReadOnlyToast(); return; }
    setLocalGuardrails(prev => ({ ...prev, stopOnReply: !prev.stopOnReply }));
  };

  const handleToggleStopOnStatusChange = () => {
    if (isReadOnly) { triggerReadOnlyToast(); return; }
    setLocalGuardrails(prev => ({
      ...prev,
      stopOnStatusChange: {
        ...prev.stopOnStatusChange,
        enabled: !prev.stopOnStatusChange.enabled
      }
    }));
  };

  const handleToggleStopOnWeekend = () => {
    if (isReadOnly) { triggerReadOnlyToast(); return; }
    setLocalGuardrails(prev => ({ ...prev, stopOnWeekend: !prev.stopOnWeekend }));
  };

  const handleToggleStatus = (status) => {
    if (isReadOnly) { triggerReadOnlyToast(); return; }
    setLocalGuardrails(prev => {
      const statuses = prev.stopOnStatusChange.statuses.includes(status)
        ? prev.stopOnStatusChange.statuses.filter(s => s !== status)
        : [...prev.stopOnStatusChange.statuses, status];
      return {
        ...prev,
        stopOnStatusChange: { ...prev.stopOnStatusChange, statuses }
      };
    });
  };

  const handleSave = () => {
    if (isReadOnly) { triggerReadOnlyToast(); return; }
    onSave(localGuardrails);
  };

  // Read-only toast notification component
  const ReadOnlyToast = showReadOnlyToast ? (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-bottom-4 duration-200">
      <div className="bg-stone-900 text-white text-xs font-medium px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2">
        <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v.01M12 9v3m0 8a9 9 0 100-18 9 9 0 000 18z" />
        </svg>
        Stop the automation before modifying guardrails.
      </div>
    </div>
  ) : null;

  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-sm rounded-2xl shadow-floating border border-notion-border flex flex-col max-h-[90vh]">
          <div className="px-5 py-4 border-b border-notion-border bg-stone-50/50 flex justify-between items-center rounded-t-2xl flex-shrink-0">
            <div>
              <h3 className="font-bold text-base text-notion-text">Global Guardrails</h3>
              <p className="text-[10px] text-stone-400">Safety rules for this workflow.</p>
            </div>
            <button onClick={onClose} className="text-stone-400">
              <X className="w-5 h-5" />
            </button>
          </div>

          {isReadOnly && (
            <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100 text-xs text-amber-700 flex items-center gap-2">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v.01M12 9v3m0 8a9 9 0 100-18 9 9 0 000 18z" />
              </svg>
              Guardrails are locked while the automation is active.
            </div>
          )}

          <div className={`p-5 space-y-6 overflow-y-auto ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
            {items.map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="relative inline-block w-9 mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={
                      index === 0 ? localGuardrails.stopOnReply :
                        index === 1 ? localGuardrails.stopOnStatusChange.enabled :
                          index === 2 ? (localGuardrails.stopOnWeekend ?? false) : false
                    }
                    onChange={
                      index === 0 ? handleToggleStopOnReply :
                        index === 1 ? handleToggleStopOnStatusChange :
                          index === 2 ? handleToggleStopOnWeekend : () => { }
                    }
                    disabled={isReadOnly}
                    className="toggle-checkbox absolute block w-4 h-4 rounded-full bg-white border-2 border-stone-300 appearance-none cursor-pointer"
                  />
                  <label className="toggle-label block overflow-hidden h-4 rounded-full bg-stone-200 cursor-pointer"></label>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-stone-700">{item.title}</h4>
                  {item.desc && <p className="text-xs text-stone-500 mt-1">{item.desc}</p>}
                  {item.checkboxes && (
                    <div className="mt-2 space-y-2">
                      {item.checkboxes.map((cb, i) => {
                        const statusKey = cb.toLowerCase().replace('stop if "', '').replace('"', '');
                        const isOuterDisabled = isReadOnly || !localGuardrails.stopOnStatusChange.enabled;
                        return (
                          <label key={i} className={`flex items-center gap-2 text-xs cursor-pointer ${isOuterDisabled ? 'text-stone-300' : 'text-stone-600'}`}>
                            <input
                              type="checkbox"
                              checked={localGuardrails.stopOnStatusChange.statuses.includes(statusKey)}
                              onChange={() => handleToggleStatus(statusKey)}
                              disabled={isOuterDisabled}
                              className="accent-stone-800 rounded"
                            /> {cb}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {/* Clickable overlay to catch taps on disabled area */}
          {isReadOnly && (
            <div
              className="absolute inset-0 z-10"
              style={{ top: '60px', bottom: '60px' }}
              onClick={triggerReadOnlyToast}
            />
          )}
          <div className="p-4 border-t border-notion-border flex-shrink-0">
            <button
              onClick={handleSave}
              disabled={isReadOnly}
              className={`w-full py-3 rounded-xl text-sm font-medium ${
                isReadOnly
                  ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                  : 'bg-black text-white'
              }`}
            >
              Save Rules
            </button>
          </div>
        </div>
        {ReadOnlyToast}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-stone-900/20 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white w-full max-w-md rounded-xl shadow-floating border border-notion-border relative">
        <div className="px-6 py-4 border-b border-notion-border flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg text-notion-text">Global Guardrails</h3>
            <p className="text-xs text-stone-400">Safety rules to auto-pause this workflow.</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isReadOnly && (
          <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-100 text-xs text-amber-700 flex items-center gap-2">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v.01M12 9v3m0 8a9 9 0 100-18 9 9 0 000 18z" />
            </svg>
            Guardrails are locked while the automation is active.
          </div>
        )}

        <div className={`p-6 space-y-6 max-h-[60vh] overflow-y-auto ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
          {items.map((item, index) => (
            <div key={index} className="flex items-start gap-4">
              <div className="relative inline-block w-8 mt-0.5 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={
                    index === 0 ? localGuardrails.stopOnReply :
                      index === 1 ? localGuardrails.stopOnStatusChange.enabled :
                        index === 2 ? (localGuardrails.stopOnWeekend ?? false) : false
                  }
                  onChange={
                    index === 0 ? handleToggleStopOnReply :
                      index === 1 ? handleToggleStopOnStatusChange :
                        index === 2 ? handleToggleStopOnWeekend : () => { }
                  }
                  disabled={isReadOnly}
                  className="toggle-checkbox absolute block w-4 h-4 rounded-full bg-white border-2 border-stone-300 appearance-none cursor-pointer"
                />
                <label className="toggle-label block overflow-hidden h-4 rounded-full bg-stone-200 cursor-pointer"></label>
              </div>
              <div>
                <h4 className="text-sm font-medium text-notion-text">{item.title}</h4>
                {item.desc && <p className="text-xs text-stone-500 mt-1">{item.desc}</p>}
                {item.checkboxes && (
                  <div className="mt-2 space-y-2">
                    {item.checkboxes.map((cb, i) => {
                      const statusKey = cb.toLowerCase().replace('stop if "', '').replace('"', '');
                      const isOuterDisabled = isReadOnly || !localGuardrails.stopOnStatusChange.enabled;
                      return (
                        <label key={i} className={`flex items-center gap-2 text-xs cursor-pointer ${isOuterDisabled ? 'text-stone-300' : 'text-stone-600'}`}>
                          <input
                            type="checkbox"
                            checked={localGuardrails.stopOnStatusChange.statuses.includes(statusKey)}
                            onChange={() => handleToggleStatus(statusKey)}
                            disabled={isOuterDisabled}
                            className="accent-stone-800 rounded"
                          /> {cb}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {/* Clickable overlay to catch clicks on disabled area */}
        {isReadOnly && (
          <div
            className="absolute z-10 left-0 right-0"
            style={{ top: '85px', bottom: '65px' }}
            onClick={triggerReadOnlyToast}
          />
        )}

        <div className="p-6 border-t border-notion-border flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-notion-border rounded text-xs font-medium text-stone-600 hover:bg-stone-50">Cancel</button>
          <button
            onClick={handleSave}
            disabled={isReadOnly}
            className={`px-4 py-2 rounded text-xs font-medium ${
              isReadOnly
                ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                : 'bg-black text-white hover:bg-stone-800'
            }`}
          >
            Save Rules
          </button>
        </div>
      </div>
      {ReadOnlyToast}
    </div>
  );
};

export default GuardrailsModal;
